import type {LegendGroup} from '@/lib/classes/Legend/LegendGroup';
import type {ActiveFiltersByLayer} from '@/lib/filters/layers/layerFilters';

import type {MVTFeature, TileFeatureCache} from './types';

/**
 * Held against the tile object rather than its id in a WeakMap, so an entry goes away when deck.gl drops the tile.
 * Nothing has to clean up after a pan or a zoom.
 */
const _filteredByTile = new WeakMap<object, TileFeatureCache>();

/**
 * Makes a key from the data that decides which features a tile shows. Updates when a layer or a legend
 * symbol is turned on or off, or an attribute filter changes, not when the selection changes.
 *
 * The key holds the shape of the legend — one bit per symbol — not which groups those symbols came from.
 */
const getFilterSignature = (
  visibleLayers: string[],
  legendGroups: LegendGroup[],
  activeFilters: ActiveFiltersByLayer = {},
): string => {
  const legend = legendGroups
    .map((group) => group.legendSymbols.map((symbol) => (symbol.visible ? '1' : '0')).join(''))
    .join('-');
  return `${visibleLayers.join(',')}|${legend}|${JSON.stringify(activeFilters)}`;
};

/**
 * The tile's filtered features. Filters again only when the tile, its data, or the signature changed.
 *
 * deck.gl rebuilds a sublayer's geometry whenever its `data` array is a different object, and for polygons
 * that means cutting every shape into triangles again. Handing back the same array turns a redraw that
 * changes no features into a color change.
 *
 * Callers hang their own lists off the returned entry — points, non-points, icon shapes — so those are
 * built once each and last as long as the entry does.
 */
const getTileFeatures = (
  tile: object,
  data: MVTFeature[],
  signature: string,
  keep: (feature: MVTFeature) => boolean,
): TileFeatureCache => {
  const cached = _filteredByTile.get(tile);
  if (cached && cached.signature === signature && cached.source === data) return cached;

  const entry: TileFeatureCache = {signature, source: data, filtered: data.filter(keep)};
  _filteredByTile.set(tile, entry);
  return entry;
};

export {
  getFilterSignature,
  getTileFeatures,
};
